import React, { useState, useEffect, useRef } from "react";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import ReusableTable from "../../dashboard/ReusableTable";
import ReusableForm from "../../common/ReusableForm";
import ReusableDeleteDialog from '../../common/ReusableDeleteDialog';
import { getRequest, postRequest } from "../../services/apiService";

export default function CategoryManager() {
  const [categories, setCategories] = useState([]);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    status_db: 1,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [deleteFormData, setDeleteFormData] = useState({ confirmation: '' });
  const toast = useRef(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const searchTimeout = useRef(null);

  const deleteFormFields = [
    {
      name: "confirmation",
      label: "Confirmation",
      type: "text",
      required: true,
      placeholder: "Type 'DELETE' to confirm",
      helpText: "This action cannot be undone. Type 'DELETE' to confirm deletion."
    }
  ];

  // 🔁 Fetch categories
  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await getRequest(`master/categories/?search=${encodeURIComponent(search)}&page=${currentPage}`);
      setCategories(Array.isArray(response.results?.data) ? response.results.data : []);
      setTotalRecords(response.count || 0);
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoading(false);
    }
  };

  // Debounced fetch on search/page change
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      fetchCategories();
    }, 700);
    return () => {
      if (searchTimeout.current) clearTimeout(searchTimeout.current);
    };
  }, [search, currentPage]);

  const handleTableSearch = (value) => setSearch(value);

  const handleSubmit = async () => {
    if (!formData.name) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Category name is required",
        life: 3000,
      });
      return;
    }

    try {
      if (isEditing) {
        await postRequest("master/categories/", {
          id: formData.id,
          name: formData.name,
          status_db: formData.status_db,
        });
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Category updated successfully",
          life: 3000,
        });
      } else {
        await postRequest("master/categories/", {
          name: formData.name,
          status_db: formData.status_db,
        });
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: "Category added successfully",
          life: 3000,
        });
        setCurrentPage(1); // Go to first page if new item added
      }
      setDialogVisible(false);
      await fetchCategories(); // 🔁 Refresh table
    } catch (error) {
      console.error("Error saving category:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to save category",
        life: 3000,
      });
    }
  };

  const handleDelete = async () => {
    if (deleteFormData.confirmation !== 'DELETE') {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Please type 'DELETE' to confirm",
        life: 3000,
      });
      return;
    }

    try {
      setLoading(true);
      await postRequest("master/categories/", {
        id: categoryToDelete.id,
        status_db: 3
      });

      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "Category deactivated successfully",
        life: 3000,
      });

      setShowDeleteModal(false);
      setCategoryToDelete(null);
      setDeleteFormData({ confirmation: '' });

      // Adjust pagination if necessary
      if (categories.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }

      await fetchCategories(); // 🔁 Refresh table

    } catch (error) {
      console.error("Error deactivating category:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to deactivate category",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setFormData({ id: null, name: "", status_db: 1 });
    setIsEditing(false);
    setDialogVisible(true);
  };

  const openEditDialog = (category) => {
    setFormData({
      id: category.id,
      name: category.name,
      status_db: category.status_db,
    });
    setIsEditing(true);
    setDialogVisible(true);
  };

  const openDeleteModal = (category) => {
    setCategoryToDelete(category);
    setDeleteFormData({ confirmation: '' });
    setShowDeleteModal(true);
  };

  const onPageChange = (event) => {
    setCurrentPage(event.page + 1);
    setRowsPerPage(event.rows);
  };

  const columns = [
    { field: "name", header: "Category Name", sortable: true },
    { field: "code", header: "Code", sortable: true },
  ];

  const actionItems = [
    {
      label: "Edit",
      icon: "pi pi-pencil",
      command: (rowData) => openEditDialog(rowData),
    },
    {
      label: "Delete",
      icon: "pi pi-trash",
      command: (rowData) => openDeleteModal(rowData),
    },
  ];

  const formFields = [
    { name: 'name', label: 'Category Name', type: 'text', required: true },
  ];

  return (
    <div className="min-h-screen bg-navy-900 text-white max-w-full">
      <ReusableTable
        title="Category Details"
        data={categories}
        setData={setCategories}
        columnFields={columns}
        setAddNewDialogVisible={openAddDialog}
        actionMenu={true}
        actionItems={actionItems}
        loading={loading}
        totalRecords={totalRecords}
        rows={rowsPerPage}
        onPageChange={onPageChange}
        first={(currentPage - 1) * rowsPerPage}
        searchValue={search}
        onSearchChange={handleTableSearch}
      />

      {dialogVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl shadow-2xl w-full mx-4 animate-fade-in max-w-2xl text-black">
            <ReusableForm
              formData={formData}
              setFormData={setFormData}
              fields={formFields}
              onSubmit={handleSubmit}
              onHide={() => setDialogVisible(false)}
              loading={loading}
              headerText={isEditing ? "Edit Category" : "Add New Category"}
            />
          </div>
        </div>
      )}

      <ReusableDeleteDialog
        visible={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onDelete={handleDelete}
        fields={deleteFormFields}
        formData={deleteFormData}
        setFormData={setDeleteFormData}
        loading={loading}
        title="Delete Category"
        description={`Are you sure you want to delete category "${categoryToDelete?.name}"? This action cannot be undone. Type 'DELETE' to confirm.`}
        deleteLabel="Delete"
      />

      <Toast ref={toast} position="top-right" />
    </div>
  );
}
